<template>
  <div>
    <h3 class="mt-5 mb-3">
      {{ $t('defaultServiceValues.type') }}
    </h3>

    <v-autocomplete
      v-model="type"
      :no-data-text="$t('defaultServiceValues.label.noService')"
      :label="$t('defaultServiceValues.label.serviceType')"
      :items="
        defaultServiceSelectedTypes.length
          ? defaultServiceSelectedTypes
          : serviceTypes
      "
      :loading="isLoading"
      :rules="[rules.required]"
      :item-text="(item) => $t(`type.${item.value}`)"
      :error="disabled"
      item-value="value"
      hide-details="auto"
      ref="serviceType"
      hide-selected
      outlined
    >
      <template slot="item" slot-scope="data">
        {{ $t(`type.${data.item.value}`) }}
      </template>
      <template slot="selection" slot-scope="data">
        {{ $t(`type.${data.item.value}`) }}
      </template>
    </v-autocomplete>

    <span v-if="disabled" class="caption error--text">
      {{ $t('defaultServiceValues.messages.serviceAlreadySelected') }}
    </span>

    <h3 class="mt-8 mb-3">
      {{ $t('defaultServiceValues.values') }}
    </h3>

    <v-row>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model.lazy="price"
          v-money="catalogCurrency()"
          :label="$t('defaultServiceValues.label.price')"
          :rules="[rules.required]"
          hide-details="auto"
          class="amount"
          ref="price"
          outlined
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="discount"
          :label="$t('defaultServiceValues.label.discount')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model.lazy="square_meter_price"
          v-money="catalogCurrency()"
          :label="$t('defaultServiceValues.label.squareMetersPrice')"
          :rules="[rules.required]"
          ref="squareMeterPrice"
          hide-details="auto"
          class="amount"
          outlined
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model.lazy="base_square_meters_size"
          v-money="decimals"
          :label="$t('defaultServiceValues.label.baseSquareMetersPrice')"
          :rules="[rules.required]"
          ref="baseSquareMetersSize"
          hide-details="auto"
          class="amount"
          suffix="m²"
          outlined
        />
      </v-col>
    </v-row>

    <h3 class="mt-6 mb-3">
      {{ $t('defaultServiceValues.taxes') }}
    </h3>

    <v-row>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="host_tax"
          :label="$t('defaultServiceValues.label.taxHost')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="displacement_tax"
          :label="$t('defaultServiceValues.label.displacementTax')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="square_meter_tax"
          :label="$t('defaultServiceValues.label.taxSquareMeters')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="furniture_tax"
          :label="$t('defaultServiceValues.label.furnitureTax')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="semi_furniture_tax"
          :label="$t('defaultServiceValues.label.semiFurnitureTax')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="house_tax"
          :label="$t('defaultServiceValues.label.houseTax')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="trousseau_tax"
          :label="$t('defaultServiceValues.label.trousseauTax')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          v-model="host_realty_feature_tax"
          :label="$t('defaultServiceValues.label.hostRealtyFeatureTax')"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <v-col cols="12" sm="6">
        <HAmount
          :label="$t('defaultServiceValues.label.urgencyTax')"
          v-model="urgency_tax"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
        />
      </v-col>
      <!-- <v-col cols="12" sm="6">
        <v-text-field
          v-model="urgency_tax"
          :label="$t('defaultServiceValues.label.urgencyTax')"
          :rules="[rules.required, rules.percent]"
          class="amount"
          hide-details="auto"
          suffix="%"
          outlined
        />
      </v-col> -->
      <v-col cols="12" sm="6">
        <HAmount
          :label="$t('defaultServiceValues.label.hostUrgencyTax')"
          :amount="host_urgency_tax"
          v-model="host_urgency_tax"
          :minAmount="0"
          :maxAmount="100"
          fullWidth
          percent
          :disabled="urgency_tax <= 0"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="urgency_hours_limit"
          :label="$t('defaultServiceValues.label.urgencyHoursLimit')"
          class="amount inputAmount mr-2"
          :rules="urgency_tax > 0 ? [rules.required] : []"
          hide-details="auto"
          suffix="h"
          outlined
          :disabled="urgency_tax <= 0"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-btn
          @click="resetDefaultServiceValues"
          elevation="0"
          x-large
          block
        >
          {{ $t('defaultServiceValues.actions.cancel') }}
        </v-btn>
      </v-col>
      <v-col>
        <v-btn
          @click="addLine"
          :disabled="disabled"
          color="secondary"
          elevation="0"
          x-large
          block
        >
          <v-icon class="mr-5" small left>mdi-table-row-plus-after</v-icon>
          {{ $t('defaultServiceValues.actions.add') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { amount, percent, required } from '@/util/errorHandling'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { catalogCurrency } from '@/util/catalog'
import { clearCurrency } from '@/util/helpers'
import { decimals } from '@/util/enums'
import { VMoney } from 'v-money'
import HAmount from '../../app/HAmount.vue'

export default {
  name: 'RepeaterServiceValues',

  directives: {
    money: VMoney
  },

  components: {
    HAmount
  },

  computed: {
    ...mapGetters([
      'serviceTypes',
      'defaultServiceValuesFields',
      'defaultServiceSelectedTypes',
    ]),

    serviceContracts() {
      return this.defaultServiceValuesFields?.service_contracts
    },
  },

  watch: {
    type(type) {
      this.hasDuplicates(type)
    },

    urgency_tax(tax) {
      if (!tax || tax <= 0) {
        this.host_urgency_tax = 0
        this.urgency_hours_limit = null
      }
    }
  },

  methods: {
    ...mapActions(['checkSelectedTypes']),
    ...mapMutations(['setDefaultServiceValuesFields', 'setShowDefaultValuesForm']),

    addLine() {
      if (!this.$parent.validate()) return

      const contract = {
        type: this.type,
        price: clearCurrency(this.price),
        discount: this.discount,
        square_meter_price: clearCurrency(this.square_meter_price),
        base_square_meters_size: clearCurrency(this.base_square_meters_size),
        host_tax: this.host_tax,
        host_realty_feature_tax: this.host_realty_feature_tax,
        displacement_tax: this.displacement_tax,
        square_meter_tax: this.square_meter_tax,
        furniture_tax: this.furniture_tax,
        semi_furniture_tax: this.semi_furniture_tax,
        house_tax: this.house_tax,
        trousseau_tax: this.trousseau_tax,
        urgency_tax: this.urgency_tax,
        host_urgency_tax: this.host_urgency_tax,
        urgency_hours_limit: this.urgency_hours_limit,
      }

      this.contracts.push(contract)

      this.setDefaultServiceValuesFields({
        service_contracts: this.contracts,
      })

      this.resetFields()
      this.scrollModalToTop()
      this.checkSelectedTypes()

      this.$parent.resetValidation()
      this.$refs.serviceType.$refs.input.focus()
    },

    resetDefaultServiceValues() {
      this.setDefaultServiceValuesFields({
        city_id: null,
        contract_type: null,
        service_contracts: [],
      })

      this.setShowDefaultValuesForm(false)
    },

    resetFields() {
      this.type = null
      this.price = null
      this.discount = 0
      this.square_meter_price = 0
      this.base_square_meters_size = null
      this.host_tax = 0
      this.displacement_tax = 0
      this.square_meter_tax = 0
      this.furniture_tax = 0
      this.semi_furniture_tax = 0
      this.house_tax = 0
      this.trousseau_tax = 0
      this.host_realty_feature_tax = 0
      this.urgency_tax = 0
      this.host_urgency_tax = 0
      this.urgency_hours_limit = null

      this.resetPrices()
    },

    resetPrices() {
      this.$refs.price.$refs.input.value = ''
      this.$refs.squareMeterPrice.$refs.input.value = ''
      this.$refs.baseSquareMetersSize.$refs.input.value = ''
    },

    scrollModalToTop() {
      const modal = this.$parent.$parent.$el.parentElement
      modal.scrollTop = 0
    },

    hasDuplicates(type) {
      const selectedTypes = this.serviceContracts.map((item) => item.type)
      this.disabled = selectedTypes.includes(type)
    },
  },

  data() {
    return {
      contracts: [],
      isLoading: false,
      disabled: false,

      type: null,
      price: null,
      discount: 0,
      square_meter_price: 0,
      base_square_meters_size: null,
      host_tax: 0,
      displacement_tax: 0,
      square_meter_tax: 0,
      furniture_tax: 0,
      semi_furniture_tax: 0,
      house_tax: 0,
      trousseau_tax: 0,
      host_realty_feature_tax: 0,
      urgency_tax: 0,
      host_urgency_tax: 0,
      urgency_hours_limit: null,

      rules: {
        amount,
        percent,
        required,
      },

      decimals,
      catalogCurrency,
      // currency,
    }
  },

  mounted() {
    this.checkSelectedTypes()
  },
}
</script>
